import {
  Component,
  forwardRef,
  Input,
  OnInit,
  ViewChild,
  ElementRef,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  EventEmitter,
  Output,
  HostBinding,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { Observable, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';

@Component({
  selector: 'app-searchable-multi-select',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatCheckboxModule,
  ],
  templateUrl: './searchable-multi-select.component.html',
  styleUrls: ['./searchable-multi-select.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchableMultiSelectComponent),
      multi: true,
    },
  ],
})
export class SearchableMultiSelectComponent implements OnInit, OnDestroy, ControlValueAccessor, OnChanges {
  @Input() label: string = '';
  @Input() placeholder: string = 'Select';
  @Input() options: string[] = [];
  @Input() inputStyle: string = '';
  @Input() required: boolean = false;
  @Input() multiple: boolean = true;
  @Input() disabledOptions: string[] = [];
  @Output() selectionChange = new EventEmitter<string[]>();

  @ViewChild('search') searchTextBox!: ElementRef;
  @ViewChild(MatSelect) matSelect!: MatSelect;

  selectFormControl = new FormControl();
  searchTextboxControl = new FormControl();

  public filteredOptions?: Observable<string[]>;
  private destroy$ = new Subject<void>();

  onChange: (value: string[]) => void = () => {};
  onTouched: () => void = () => {};

  private setupFilteredOptions() {
    this.filteredOptions = this.searchTextboxControl.valueChanges.pipe(
      startWith(''),
      map((value) => this._filter(value))
    );
  }

  ngOnInit() {
    this.setupFilteredOptions();

    // When the selection changes, propagate it to the parent form
    this.selectFormControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.onChange(value);
      this.selectionChange.emit(value);
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.setupFilteredOptions();
  }

  writeValue(value: string[]): void {
    this.selectFormControl.setValue(value ?? [], { emitEvent: false });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  // --- Component Logic ---
  private _filter(value: string): string[] {
    const filterValue = value ? value.toLowerCase() : '';
    const filtered = this.options.filter((o) => o.toLowerCase().includes(filterValue));
    const selected = this.selectFormControl.value ?? [];

    // ensure selected options always remain in the list
    return Array.from(new Set([...selected, ...filtered]));
  }

  onPanelOpen() {
    this.searchTextboxControl.setValue('');
    setTimeout(() => this.searchTextBox.nativeElement.focus(), 100);
  }

  onPanelClose() {
    this.onTouched();
  }

  clearSearch(event: MouseEvent) {
    event.stopPropagation();
    this.searchTextboxControl.setValue('');
  }
  @HostBinding('class.is-required')
  get isRequired() {
    return this.required;
  }
  compareValues(a: string, b: string) {
    return a === b;
  }
  isDisabled(option: string): boolean {
    return this.disabledOptions ? this.disabledOptions.includes(option) : false;
  }
}
